import { useEffect, useRef, useState } from "react";

interface Props {
  value: number; // 0..100
  size?: number;
  label?: string;
}

function colorFor(v: number): [string, string] {
  if (v >= 80) return ["#5eead4", "#30d158"];
  if (v >= 60) return ["#7cc4ff", "#5eead4"];
  if (v >= 40) return ["#ffd60a", "#ff9f0a"];
  return ["#ff9f0a", "#ff453a"];
}

/** Animated gradient progress ring with a counting-up number. */
export function ScoreRing({ value, size = 132, label = "Gait Score" }: Props) {
  const [shown, setShown] = useState(0);
  const raf = useRef(0);
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const [g1, g2] = colorFor(value);
  const id = `ring-${Math.round(value)}-${size}`;

  useEffect(() => {
    const start = performance.now();
    const dur = 1100;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(value * eased);
      if (t < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [value]);

  const offset = c * (1 - shown / 100);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flex: "0 0 auto" }}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={g1} />
          <stop offset="100%" stopColor={g2} />
        </linearGradient>
        <filter id={`${id}-glow`}>
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={`url(#${id})`}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        filter={`url(#${id}-glow)`}
      />
      <text x="50%" y="48%" textAnchor="middle" dominantBaseline="middle" fontSize={size * 0.28} fontWeight="800" fill="#f5f5f7">
        {Math.round(shown)}
      </text>
      <text x="50%" y="66%" textAnchor="middle" dominantBaseline="middle" fontSize={size * 0.09} fill="rgba(245,245,247,0.5)" letterSpacing="1.5">
        {label.toUpperCase()}
      </text>
    </svg>
  );
}
